import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Search, Navigation, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
const competitorColors: Record<string, string> = {
  Bauhaus: '#e0001d',
  Hornbach: '#f89314',
  toom: '#ba0010',
  Hagebau: '#009745'
};
const competitorLogos: Record<string, string> = {
  Bauhaus: 'https://upload.wikimedia.org/wikipedia/de/thumb/4/4b/Bauhaus_%28Baumarkt%29_logo.svg/330px-Bauhaus_%28Baumarkt%29_logo.svg.png',
  Hornbach: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/dc/Hornbach_Logo_black.svg/330px-Hornbach_Logo_black.svg.png',
  toom: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Toom_Baumarkt.svg/330px-Toom_Baumarkt.svg.png',
  Hagebau: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Hagebau_Logo_2022.svg/330px-Hagebau_Logo_2022.svg.png'
};
interface TopicItem {
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
}
const competitorTopics: Array<{
  name: string;
  topics: TopicItem[];
}> = [{
  name: 'Bauhaus',
  topics: [{
    name: 'Sortimentsbreite',
    size: 845,
    sentiment: 'positive'
  }, {
    name: 'Drive-In Arena',
    size: 620,
    sentiment: 'positive'
  }, {
    name: 'Gartenabteilung',
    size: 590,
    sentiment: 'positive'
  }, {
    name: 'Hohe Preise',
    size: 450,
    sentiment: 'negative'
  }, {
    name: 'Beratung fehlt',
    size: 380,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 310,
    sentiment: 'neutral'
  }, {
    name: 'Öffnungszeiten',
    size: 280,
    sentiment: 'neutral'
  }]
}, {
  name: 'Hornbach',
  topics: [{
    name: 'Projektberatung',
    size: 920,
    sentiment: 'positive'
  }, {
    name: 'Dauertiefpreise',
    size: 750,
    sentiment: 'positive'
  }, {
    name: 'Holzzuschnitt',
    size: 610,
    sentiment: 'positive'
  }, {
    name: 'Baustoffe',
    size: 540,
    sentiment: 'positive'
  }, {
    name: 'Wartezeit Kasse',
    size: 480,
    sentiment: 'negative'
  }, {
    name: 'Unübersichtlich',
    size: 420,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 320,
    sentiment: 'neutral'
  }, {
    name: 'Standort',
    size: 310,
    sentiment: 'neutral'
  }]
}, {
  name: 'toom',
  topics: [{
    name: 'Pflanzenqualität',
    size: 880,
    sentiment: 'positive'
  }, {
    name: 'Sauberkeit',
    size: 710,
    sentiment: 'positive'
  }, {
    name: 'Eigenmarken',
    size: 650,
    sentiment: 'positive'
  }, {
    name: 'Beratung',
    size: 520,
    sentiment: 'negative'
  }, {
    name: 'Sortimentstiefe',
    size: 410,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 390,
    sentiment: 'neutral'
  }, {
    name: 'Preise',
    size: 250,
    sentiment: 'neutral'
  }]
}, {
  name: 'Hagebau',
  topics: [{
    name: 'Holzabteilung',
    size: 890,
    sentiment: 'positive'
  }, {
    name: 'Personal',
    size: 760,
    sentiment: 'positive'
  }, {
    name: 'Kundenkarte',
    size: 620,
    sentiment: 'positive'
  }, {
    name: 'Atmosphäre',
    size: 580,
    sentiment: 'negative'
  }, {
    name: 'Auswahl',
    size: 490,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 450,
    sentiment: 'neutral'
  }, {
    name: 'Wartezeiten',
    size: 350,
    sentiment: 'negative'
  }, {
    name: 'Öffnungszeiten',
    size: 320,
    sentiment: 'neutral'
  }]
}];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const radiusTopicData: Record<string, Array<{
  name: string;
  topics: TopicItem[];
}>> = {
  'Hamburg, Jungfernstieg': [{
    name: 'Bauhaus',
    topics: [{
      name: 'Preise',
      size: 58,
      sentiment: 'negative'
    }, {
      name: 'City-Lage',
      size: 45,
      sentiment: 'positive'
    }, {
      name: 'Eng',
      size: 41,
      sentiment: 'negative'
    }, {
      name: 'Werkzeug',
      size: 32,
      sentiment: 'positive'
    }]
  }, {
    name: 'Hornbach',
    topics: [{
      name: 'Wartezeit',
      size: 44,
      sentiment: 'negative'
    }, {
      name: 'Holz',
      size: 38,
      sentiment: 'positive'
    }, {
      name: 'Unordentlich',
      size: 35,
      sentiment: 'negative'
    }, {
      name: 'Beratung',
      size: 29,
      sentiment: 'positive'
    }]
  }, {
    name: 'toom',
    topics: [{
      name: 'Auswahl',
      size: 61,
      sentiment: 'negative'
    }, {
      name: 'Pflanzen',
      size: 52,
      sentiment: 'positive'
    }, {
      name: 'Personal',
      size: 28,
      sentiment: 'negative'
    }]
  }],
  'Berlin, Alexanderplatz': [{
    name: 'Bauhaus',
    topics: [{
      name: 'Überfüllt',
      size: 92,
      sentiment: 'negative'
    }, {
      name: 'Öffnungszeiten',
      size: 88,
      sentiment: 'positive'
    }, {
      name: 'Preise',
      size: 65,
      sentiment: 'negative'
    }, {
      name: 'Farben',
      size: 45,
      sentiment: 'positive'
    }]
  }, {
    name: 'Hornbach',
    topics: [{
      name: 'Sauberkeit',
      size: 81,
      sentiment: 'negative'
    }, {
      name: 'Preise',
      size: 75,
      sentiment: 'positive'
    }, {
      name: 'Kassen',
      size: 66,
      sentiment: 'negative'
    }, {
      name: 'Baustoffe',
      size: 54,
      sentiment: 'positive'
    }]
  }, {
    name: 'Hagebau',
    topics: [{
      name: 'Sortiment',
      size: 77,
      sentiment: 'negative'
    }, {
      name: 'Wartezeit',
      size: 59,
      sentiment: 'negative'
    }, {
      name: 'Zentral',
      size: 42,
      sentiment: 'positive'
    }]
  }],
  'München, Marienplatz': [{
    name: 'Bauhaus',
    topics: [{
      name: 'Teuer',
      size: 88,
      sentiment: 'negative'
    }, {
      name: 'Auswahl',
      size: 65,
      sentiment: 'positive'
    }, {
      name: 'Qualität',
      size: 52,
      sentiment: 'positive'
    }, {
      name: 'Kassen',
      size: 45,
      sentiment: 'negative'
    }]
  }, {
    name: 'Hornbach',
    topics: [{
      name: 'Atmosphäre',
      size: 55,
      sentiment: 'negative'
    }, {
      name: 'Holz',
      size: 48,
      sentiment: 'positive'
    }, {
      name: 'Preise',
      size: 41,
      sentiment: 'positive'
    }, {
      name: 'Personal',
      size: 32,
      sentiment: 'negative'
    }]
  }, {
    name: 'toom',
    topics: [{
      name: 'Parken',
      size: 71,
      sentiment: 'negative'
    }, {
      name: 'Sauberkeit',
      size: 62,
      sentiment: 'positive'
    }, {
      name: 'Pflanzen',
      size: 55,
      sentiment: 'positive'
    }, {
      name: 'Marken',
      size: 38,
      sentiment: 'negative'
    }]
  }]
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string): number {
  if (period === 'gesamt') return value;
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
function SimpleTreemap({
  data
}: {
  data: TopicItem[];
}) {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-1.5 h-full" data-id="element-915">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1,
        rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} className="rounded-lg p-2 flex flex-col justify-center items-center text-center" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-916">
            <span className="font-semibold text-xs leading-tight" style={{
          color: colors.text
        }} data-id="element-917">
              {item.name}
            </span>
            <span className="text-[10px] mt-0.5 opacity-70" style={{
          color: colors.text
        }} data-id="element-918">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
}
export function ThemenvergleichPage() {
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const handleSearch = () => {
    const key = Object.keys(radiusTopicData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  const rawLocalTopics = radiusTopicData[addressSearched] || radiusTopicData['Hamburg, Jungfernstieg'];
  const currentCompetitorTopics = competitorTopics.map(comp => ({
    ...comp,
    topics: comp.topics.map(t => ({
      ...t,
      size: applyTimeFactor(t.size, selectedPeriod)
    }))
  }));
  const localTopics = rawLocalTopics.map(comp => ({
    ...comp,
    topics: comp.topics.map(t => ({
      ...t,
      size: applyTimeFactor(t.size, selectedPeriod)
    }))
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-919">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-920">
        <div data-id="element-921">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-922">Themenvergleich</h1>
          <p className="text-gray-500 mt-1" data-id="element-923">
            Stärken und Schwächen der Mitbewerber basierend auf Kundenfeedback
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white p-2 rounded-xl shadow-sm border border-gray-100" data-id="element-924">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600 pl-2" data-id="element-925">
            <Calendar size={16} data-id="element-926" />
            <span data-id="element-927">Zeitraum:</span>
          </div>
          <div className="flex gap-1" data-id="element-928">
            {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedPeriod === period.id ? 'bg-obi-orange text-white shadow-sm' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-929">
                {period.label}
              </button>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-930">
        {currentCompetitorTopics.map((comp, index) => <motion.div key={comp.name} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} data-id="element-931">
            <Card className="h-full" data-id="element-932">
              <div className="p-6" data-id="element-933">
                <div className="flex items-center gap-3 mb-4" data-id="element-934">
                  <img src={competitorLogos[comp.name]} alt={comp.name} className="h-7 w-auto max-w-[80px] object-contain shrink-0" data-id="element-935" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-936">
                    {comp.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-937">
                  Häufig erwähnte Themen in Bewertungen
                </p>

                <div className="h-56" data-id="element-938">
                  <SimpleTreemap data={comp.topics} data-id="element-939" />
                </div>

                <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-940">
                  <div className="flex items-center gap-2" data-id="element-941">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#639754'
                }} data-id="element-942" />
                    <span className="text-xs text-gray-600" data-id="element-943">Positiv</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-944">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-945" />
                    <span className="text-xs text-gray-600" data-id="element-946">Neutral</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-947">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-948" />
                    <span className="text-xs text-gray-600" data-id="element-949">Negativ</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>)}
      </div>

      <Card data-id="element-950">
        <div className="p-6" data-id="element-951">
          <div className="mb-5" data-id="element-952">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-953">
              Lokale Themenanalyse
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-954">
              Welche Themen bewegen die Kunden im direkten Umfeld?
            </p>
          </div>

          <div className="flex gap-3 mb-6 flex-wrap" data-id="element-955">
            <div className="flex-1 min-w-48 relative" data-id="element-956">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-957" />
              <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-obi-orange/20 focus:border-obi-orange" data-id="element-958" />
            </div>
            <div className="flex items-center gap-2" data-id="element-959">
              <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-960">
                Radius:
              </span>
              <div className="flex gap-1" data-id="element-961">
                {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-obi-orange text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-962">
                    {r}
                  </button>)}
              </div>
            </div>
            <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-obi-orange text-white rounded-lg text-sm font-medium hover:bg-obi-dark transition-colors" data-id="element-963">
              <Search size={14} data-id="element-964" /> Suchen
            </button>
          </div>

          {hasSearched && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-965">
              <div className="text-xs text-gray-500 mb-6 flex items-center gap-1.5" data-id="element-966">
                <Navigation size={12} data-id="element-967" />
                <span data-id="element-968">
                  Ergebnisse für{' '}
                  <strong className="text-gray-700" data-id="element-969">{addressSearched}</strong>{' '}
                  im Umkreis von{' '}
                  <strong className="text-gray-700" data-id="element-970">{selectedRadius}</strong>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-971">
                {localTopics.map((comp, i) => <motion.div key={comp.name} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} className="rounded-xl border border-gray-100 bg-gray-50/50 p-4" data-id="element-972">
                    <div className="flex items-center gap-2 mb-4" data-id="element-973">
                      <img src={competitorLogos[comp.name]} alt={comp.name} className="h-5 w-auto max-w-[60px] object-contain shrink-0" data-id="element-974" />
                      <span className="font-semibold text-gray-900" data-id="element-975">
                        {comp.name}
                      </span>
                    </div>

                    <div className="h-40" data-id="element-976">
                      <SimpleTreemap data={comp.topics} data-id="element-977" />
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>}
        </div>
      </Card>
    </div>;
}